import "./CompanyInventoryCounts.css";

import {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import axios from "axios";
import { toast } from "react-toastify";

import { usePermissions } from "./PermissionContext";

import {
  downloadInventoryCountExcel,
} from "./utils/inventoryCountExcel";

const API_URL = "/api";

const getHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem(
    "company_token"
  )}`,
});

const today = () =>
  new Date().toISOString().slice(0, 10);

const emptyForm = () => ({
  warehouse_id: "",
  count_date: today(),
  category: "",
  is_blind: false,
  notes: "",
});

const weekDays = [
  { value: "0", label: "Lunes" },
  { value: "1", label: "Martes" },
  { value: "2", label: "Miércoles" },
  { value: "3", label: "Jueves" },
  { value: "4", label: "Viernes" },
  { value: "5", label: "Sábado" },
  { value: "6", label: "Domingo" },
];

const emptyScheduleForm = () => ({
  warehouse_id: "",
  frequency: "weekly",
  weekday: "0",
  month_day: "1",
  category: "",
  is_blind: false,
  notes: "",
});

const getErrorMessage = (error) => {
  const detail = error.response?.data?.detail;

  if (typeof detail === "string") return detail;

  if (Array.isArray(detail)) {
    return detail.map((item) => item.msg).join(". ");
  }

  return "No fue posible completar la operación.";
};

const formatDate = (value) => {
  if (!value) return "";

  return new Date(`${value}T00:00:00`).toLocaleDateString(
    "es-CO"
  );
};

const formatDateTime = (value) => {
  if (!value) return "";

  return new Date(value).toLocaleString("es-CO");
};

const formatQuantity = (value) =>
  new Intl.NumberFormat("es-CO", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 3,
  }).format(Number(value || 0));

const formatInputQuantity = (value) => {
  if (value === null || value === undefined || value === "") {
    return "";
  }

  const numericValue = Number(value);

  return Number.isFinite(numericValue)
    ? String(numericValue)
    : "";
};

const escapeHtml = (value) =>
  String(value ?? "").replace(
    /[&<>"']/g,
    (character) =>
      ({
        "&": "&amp;",
        "<": "&lt;",
        ">": "&gt;",
        '"': "&quot;",
        "'": "&#039;",
      })[character]
  );

const getLogoUrl = (logo) => {
  if (!logo) return "";

  if (
    logo.startsWith("http://") ||
    logo.startsWith("https://") ||
    logo.startsWith("data:")
  ) {
    return logo;
  }

  return `${API_URL}${logo.startsWith("/") ? logo : `/${logo}`}`;
};

function CompanyInventoryCounts() {
  const { can } = usePermissions();

  const canManageInventory = can("inventory.manage");
  const canApproveInventory = can("inventory.approve");

  const [warehouses, setWarehouses] = useState([]);
  const [counts, setCounts] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [scheduleForm, setScheduleForm] = useState(
    emptyScheduleForm
  );
  const [savingSchedule, setSavingSchedule] =
    useState(false);
  const [runningScheduleId, setRunningScheduleId] =
    useState(null);
  const [countForm, setCountForm] = useState(emptyForm);

  const [selectedCount, setSelectedCount] = useState(null);
  const [itemValues, setItemValues] = useState({});
  const [itemSearch, setItemSearch] = useState("");

  const [loading, setLoading] = useState(true);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [savingCount, setSavingCount] = useState(false);
  const [savingItemId, setSavingItemId] = useState(null);

  const [confirmation, setConfirmation] = useState(null);
  const [processingConfirmation, setProcessingConfirmation] =
    useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);

    try {
      const schedulesRequest = canManageInventory
        ? axios.get(
          `${API_URL}/inventory-counts/me/schedules`,
          {
            headers: getHeaders(),
          }
        )
        : Promise.resolve({ data: [] });

      const [
        warehousesResponse,
        countsResponse,
        schedulesResponse,
      ] = await Promise.all([
        axios.get(`${API_URL}/warehouses/me`, {
          headers: getHeaders(),
          params: { active_only: true },
        }),
        axios.get(`${API_URL}/inventory-counts/me`, {
          headers: getHeaders(),
        }),
        schedulesRequest,
      ]);

      setWarehouses(warehousesResponse.data);
      setCounts(countsResponse.data);
      setSchedules(schedulesResponse.data);
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  }, [canManageInventory]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const loadCountDetail = async (countId) => {
    setLoadingDetail(true);

    try {
      const response = await axios.get(
        `${API_URL}/inventory-counts/me/${countId}`,
        { headers: getHeaders() }
      );

      setSelectedCount(response.data);

      setItemValues(
        response.data.items.reduce((result, item) => {
          result[item.id] = formatInputQuantity(
            item.counted_quantity
          );

          return result;
        }, {})
      );
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setLoadingDetail(false);
    }
  };

  const closeCountDetail = () => {
    setSelectedCount(null);
    setItemValues({});
    setItemSearch("");
  };

  const handleFormChange = (event) => {
    const { name, value, type, checked } = event.target;

    setCountForm((current) => ({
      ...current,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const createCount = async (event) => {
    event.preventDefault();

    if (!canManageInventory) {
      toast.error(
        "No tienes permiso para abrir conteos físicos."
      );
      return;
    }

    if (!countForm.warehouse_id) {
      toast.error("Selecciona una bodega.");
      return;
    }

    setSavingCount(true);

    try {
      const response = await axios.post(
        `${API_URL}/inventory-counts/me`,
        {
          warehouse_id: Number(countForm.warehouse_id),
          count_date: countForm.count_date,
          category: countForm.category.trim() || null,
          is_blind: countForm.is_blind,
          notes: countForm.notes.trim() || null,
        },
        { headers: getHeaders() }
      );

      setSelectedCount(response.data);

      setItemValues(
        response.data.items.reduce((result, item) => {
          result[item.id] = "";
          return result;
        }, {})
      );

      setCountForm(emptyForm());

      toast.success(
        response.data.is_blind
          ? "Conteo ciego abierto. El teórico está oculto."
          : "Conteo físico abierto correctamente."
      );

      await loadData();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setSavingCount(false);
    }
  };

  const handleScheduleFormChange = (event) => {
    const { name, value, type, checked } = event.target;

    setScheduleForm((current) => ({
      ...current,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const createSchedule = async (event) => {
    event.preventDefault();

    if (!scheduleForm.warehouse_id) {
      toast.error(
        "Selecciona la bodega para la programación."
      );
      return;
    }

    setSavingSchedule(true);

    try {
      await axios.post(
        `${API_URL}/inventory-counts/me/schedules`,
        {
          warehouse_id: Number(scheduleForm.warehouse_id),
          frequency: scheduleForm.frequency,
          weekday:
            scheduleForm.frequency === "weekly"
              ? Number(scheduleForm.weekday)
              : null,
          month_day:
            scheduleForm.frequency === "monthly"
              ? Number(scheduleForm.month_day)
              : null,
          category: scheduleForm.category.trim() || null,
          is_blind: scheduleForm.is_blind,
          notes: scheduleForm.notes.trim() || null,
        },
        {
          headers: getHeaders(),
        }
      );

      setScheduleForm(emptyScheduleForm());

      toast.success(
        "Programación de conteo creada correctamente."
      );

      await loadData();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setSavingSchedule(false);
    }
  };

  const toggleScheduleStatus = async (schedule) => {
    try {
      await axios.patch(
        `${API_URL}/inventory-counts/me/schedules/${schedule.id}`,
        {
          is_active: !schedule.is_active,
        },
        {
          headers: getHeaders(),
        }
      );

      toast.success(
        schedule.is_active
          ? "Programación pausada."
          : "Programación activada."
      );

      await loadData();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const runScheduleNow = async (schedule) => {
    setRunningScheduleId(schedule.id);

    try {
      const response = await axios.post(
        `${API_URL}/inventory-counts/me/schedules/${schedule.id}/run`,
        {},
        {
          headers: getHeaders(),
        }
      );

      setSelectedCount(response.data);

      setItemValues(
        response.data.items.reduce((result, item) => {
          result[item.id] = formatInputQuantity(
            item.counted_quantity
          );

          return result;
        }, {})
      );

      toast.success(
        `Conteo ${response.data.count_number} abierto desde la programación.`
      );

      await loadData();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setRunningScheduleId(null);
    }
  };

  const saveItemCount = async (item) => {
    const countedQuantity = itemValues[item.id];

    if (
      countedQuantity === "" ||
      Number.isNaN(Number(countedQuantity)) ||
      Number(countedQuantity) < 0
    ) {
      toast.error(
        `Ingresa una cantidad válida para "${item.product_name}".`
      );
      return;
    }

    setSavingItemId(item.id);

    try {
      const response = await axios.patch(
        `${API_URL}/inventory-counts/me/${selectedCount.id}/items/${item.id}`,
        {
          counted_quantity: Number(countedQuantity),
        },
        { headers: getHeaders() }
      );

      setSelectedCount(response.data);

      toast.success(
        `Cantidad registrada: ${item.product_name}.`
      );

      await loadData();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setSavingItemId(null);
    }
  };

  const requestSubmitCount = () => {
    if (!selectedCount) return;

    if (selectedCount.pending_items > 0) {
      toast.error(
        `Faltan ${selectedCount.pending_items} productos por contar.`
      );
      return;
    }

    setConfirmation({
      type: "submit",
      title: "Enviar conteo a aprobación",
      message:
        "El conteo quedará bloqueado para edición hasta que un supervisor lo apruebe o lo devuelva para corrección.",
      confirmLabel: "Sí, enviar a aprobación",
    });
  };

  const requestCancelCount = () => {
    if (!selectedCount) return;

    setConfirmation({
      type: "cancel",
      title: "Cancelar conteo físico",
      message:
        "El conteo quedará cancelado y no generará ajustes de inventario.",
      confirmLabel: "Sí, cancelar conteo",
    });
  };

  const requestApproveCount = () => {
    if (!selectedCount) return;

    setConfirmation({
      type: "approve",
      title: "Aprobar y ajustar inventario",
      message:
        "Se aplicarán los movimientos de entrada o salida necesarios según las cantidades físicas registradas.",
      confirmLabel: "Sí, aprobar y ajustar",
    });
  };

  const requestRejectCount = () => {
    if (!selectedCount) return;

    setConfirmation({
      type: "reject",
      title: "Devolver para corrección",
      message:
        "El conteo volverá a estar disponible para que el operario corrija o complete la información.",
      confirmLabel: "Devolver conteo",
      reason: "",
    });
  };

  const confirmCountAction = async () => {
    if (!selectedCount || !confirmation) return;

    if (
      confirmation.type === "reject" &&
      !confirmation.reason?.trim()
    ) {
      toast.error(
        "Escribe el motivo por el cual devuelves el conteo."
      );
      return;
    }

    setProcessingConfirmation(true);

    try {
      let response;
      let successMessage;

      if (confirmation.type === "submit") {
        response = await axios.post(
          `${API_URL}/inventory-counts/me/${selectedCount.id}/submit`,
          {},
          { headers: getHeaders() }
        );

        successMessage =
          "Conteo enviado correctamente a aprobación.";
      }

      if (confirmation.type === "cancel") {
        response = await axios.post(
          `${API_URL}/inventory-counts/me/${selectedCount.id}/cancel`,
          {},
          { headers: getHeaders() }
        );

        successMessage = "Conteo físico cancelado.";
      }

      if (confirmation.type === "approve") {
        response = await axios.post(
          `${API_URL}/inventory-counts/me/${selectedCount.id}/approve`,
          {},
          { headers: getHeaders() }
        );

        successMessage =
          "Conteo aprobado. Los ajustes fueron aplicados.";
      }

      if (confirmation.type === "reject") {
        response = await axios.post(
          `${API_URL}/inventory-counts/me/${selectedCount.id}/reject`,
          {
            reason: confirmation.reason.trim(),
          },
          { headers: getHeaders() }
        );

        successMessage =
          "Conteo devuelto para corrección.";
      }

      toast.success(successMessage);
      setConfirmation(null);

      if (confirmation.type === "cancel") {
        closeCountDetail();
      } else {
        setSelectedCount(response.data);

        setItemValues(
          response.data.items.reduce((result, item) => {
            result[item.id] = formatInputQuantity(
              item.counted_quantity
            );

            return result;
          }, {})
        );
      }

      await loadData();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setProcessingConfirmation(false);
    }
  };

  const printCountReceipt = async () => {
    if (!selectedCount) return;

    const printWindow = window.open(
      "",
      "_blank",
      "width=1000,height=850"
    );

    if (!printWindow) {
      toast.error(
        "Permite las ventanas emergentes para imprimir el comprobante."
      );
      return;
    }

    printWindow.document.write(
      "<p style='font-family:Arial;padding:24px'>Generando comprobante...</p>"
    );

    try {
      const response = await axios.get(
        `${API_URL}/inventory-counts/me/${selectedCount.id}/receipt`,
        { headers: getHeaders() }
      );

      const receipt = response.data;
      const logoUrl = getLogoUrl(receipt.company?.logo);

      const rows = receipt.items
        .map((item) => {
          const adjustment = Number(
            item.adjustment_quantity || 0
          );

          const adjustmentClass =
            adjustment > 0
              ? "positive"
              : adjustment < 0
                ? "negative"
                : "";

          return `
            <tr>
              <td>
                <strong>${escapeHtml(item.product_name)}</strong>
                <small>${escapeHtml(item.product_sku)}</small>
              </td>
              <td>${formatQuantity(
            item.system_quantity_at_start
          )}</td>
              <td>${formatQuantity(
            item.counted_quantity
          )}</td>
              <td>${formatQuantity(
            item.system_quantity_at_close
          )}</td>
              <td class="${adjustmentClass}">
                ${adjustment > 0 ? "+" : ""}${formatQuantity(
            adjustment
          )}
              </td>
            </tr>
          `;
        })
        .join("");

      printWindow.document.open();
      printWindow.document.write(`
        <!DOCTYPE html>
        <html lang="es">
          <head>
            <meta charset="UTF-8" />
            <title>Comprobante ${escapeHtml(
        receipt.count_number
      )}</title>
            <style>
              @page { size: A4; margin: 12mm; }
              * { box-sizing: border-box; }
              body {
                margin: 0;
                color: #1b3151;
                background: #fff;
                font: 12px Arial, Helvetica, sans-serif;
              }
              .header {
                display: flex;
                justify-content: space-between;
                gap: 20px;
                padding-bottom: 16px;
                border-bottom: 3px solid #e600a9;
              }
              .company {
                display: flex;
                align-items: center;
                gap: 12px;
              }
              .logo {
                width: 62px;
                height: 62px;
                border: 1px solid #dce6f0;
                border-radius: 9px;
                object-fit: contain;
              }
              h1, h2, p { margin: 0; }
              h1 { font-size: 19px; }
              h2 { margin-top: 5px; font-size: 21px; }
              .muted {
                margin-top: 4px;
                color: #687b95;
                font-size: 10px;
              }
              .document { text-align: right; }
              .label {
                color: #e600a9;
                font-size: 9px;
                font-weight: 800;
                letter-spacing: .1em;
              }
              .status {
                display: inline-block;
                margin-top: 8px;
                padding: 5px 10px;
                border-radius: 999px;
                background: #d9f8e5;
                color: #08783b;
                font-size: 10px;
                font-weight: 800;
              }
              .grid {
                display: grid;
                grid-template-columns: repeat(3, 1fr);
                gap: 10px;
                margin: 18px 0;
              }
              .card {
                padding: 11px;
                border: 1px solid #dce6f0;
                border-radius: 8px;
                background: #f8fbff;
              }
              .card span {
                display: block;
                color: #71839b;
                font-size: 9px;
                font-weight: 800;
                text-transform: uppercase;
              }
              .card strong {
                display: block;
                margin-top: 7px;
                font-size: 12px;
              }
              .notes {
                margin-bottom: 17px;
                padding: 11px;
                border-left: 3px solid #e600a9;
                background: #fff8fd;
              }
              .section {
                margin: 20px 0 7px;
                color: #e600a9;
                font-size: 9px;
                font-weight: 800;
                letter-spacing: .1em;
              }
              table {
                width: 100%;
                border-collapse: collapse;
              }
              th {
                padding: 10px;
                background: #e600a9;
                color: white;
                font-size: 10px;
                text-align: left;
              }
              td {
                padding: 10px;
                border-bottom: 1px solid #dce6f0;
                color: #425976;
              }
              td small {
                display: block;
                margin-top: 3px;
                color: #8493a8;
                font-size: 9px;
              }
              .positive { color: #08783b; font-weight: 800; }
              .negative { color: #c32929; font-weight: 800; }
              .responsibles {
                display: grid;
                grid-template-columns: 1fr 1fr;
                gap: 10px;
                margin-top: 22px;
              }
              footer {
                margin-top: 22px;
                padding-top: 10px;
                border-top: 1px solid #dce6f0;
                color: #8796aa;
                font-size: 9px;
                text-align: center;
              }
            </style>
          </head>
          <body>
            <main>
              <header class="header">
                <div class="company">
                  ${logoUrl
          ? `<img class="logo" src="${escapeHtml(
            logoUrl
          )}" alt="Logo" />`
          : ""
        }
                  <div>
                    <h1>${escapeHtml(
          receipt.company?.company_name
        )}</h1>
                    <p class="muted">
                      ${escapeHtml(
          [
            receipt.company?.nit
              ? `NIT: ${receipt.company.nit}`
              : "",
            receipt.company?.address,
            receipt.company?.city,
            receipt.company?.phone ||
            receipt.company?.mobile,
            receipt.company?.email,
          ]
            .filter(Boolean)
            .join(" · ")
        )}
                    </p>
                  </div>
                </div>

                <div class="document">
                  <p class="label">COMPROBANTE INTERNO</p>
                  <h2>${escapeHtml(receipt.count_number)}</h2>
                  <span class="status">COMPLETADO</span>
                </div>
              </header>

              <section class="grid">
                <article class="card">
                  <span>Fecha de conteo</span>
                  <strong>${formatDate(
          receipt.count_date
        )}</strong>
                </article>
                <article class="card">
                  <span>Bodega</span>
                  <strong>${escapeHtml(
          receipt.warehouse?.name
        )}</strong>
                </article>
                <article class="card">
                  <span>Fecha de aprobación</span>
                  <strong>${formatDateTime(
          receipt.approved_at ||
          receipt.completed_at
        )}</strong>
                </article>
              </section>

              ${receipt.notes
          ? `<p class="notes">
                      <strong>OBSERVACIONES</strong><br />
                      ${escapeHtml(receipt.notes)}
                    </p>`
          : ""
        }

              <p class="section">DETALLE DEL CONTEO</p>

              <table>
                <thead>
                  <tr>
                    <th>Producto</th>
                    <th>Teórico inicial</th>
                    <th>Físico contado</th>
                    <th>Teórico al cierre</th>
                    <th>Ajuste aplicado</th>
                  </tr>
                </thead>
                <tbody>${rows}</tbody>
              </table>

              <section class="responsibles">
                <article class="card">
                  <span>Conteo creado por</span>
                  <strong>${escapeHtml(
          receipt.created_by?.name
        )}</strong>
                  <p class="muted">${escapeHtml(
          receipt.created_by?.email
        )}</p>
                </article>

                <article class="card">
                  <span>Aprobado por</span>
                  <strong>${escapeHtml(
          receipt.approved_by?.name ||
          receipt.completed_by?.name ||
          "No disponible"
        )}</strong>
                  <p class="muted">${escapeHtml(
          receipt.approved_by?.email ||
          receipt.completed_by?.email
        )}</p>
                </article>
              </section>

              <footer>
                COMPROBANTE DE CONTEO FÍSICO · Generado por NetLedger
              </footer>
            </main>

            <script>
              window.onload = () => window.print();
            </script>
          </body>
        </html>
      `);

      printWindow.document.close();
    } catch (error) {
      printWindow.close();
      toast.error(getErrorMessage(error));
    }
  };

  const exportCountToExcel = async () => {
    if (!selectedCount) return;

    try {
      const response = await axios.get(
        `${API_URL}/inventory-counts/me/${selectedCount.id}/receipt`,
        {
          headers: getHeaders(),
        }
      );

      await downloadInventoryCountExcel({
        receipt: response.data,
        statusLabel,
      });

      toast.success(
        "Archivo Excel profesional descargado correctamente."
      );
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const statusLabel = (countStatus) => {
    const labels = {
      draft: "Borrador",
      in_progress: "En progreso",
      pending_approval: "Pendiente de aprobación",
      completed: "Completado",
      cancelled: "Cancelado",
    };

    return labels[countStatus] || countStatus;
  };

  const filteredItems = useMemo(() => {
    if (!selectedCount) return [];

    const search = itemSearch.trim().toLowerCase();

    if (!search) return selectedCount.items;

    return selectedCount.items.filter((item) =>
      `${item.product_name} ${item.product_sku}`
        .toLowerCase()
        .includes(search)
    );
  }, [itemSearch, selectedCount]);

  return (
    <div className="inventory-counts">
      <section className="inventory-counts-heading">
        <div>
          <p className="inventory-counts-kicker">
            Inventario
          </p>
          <h1>Conteos físicos</h1>
          <p>
            Compara existencias físicas, solicita aprobación
            y conserva la trazabilidad de cada ajuste.
          </p>
        </div>

        <button
          type="button"
          className="btn-count-refresh"
          onClick={loadData}
          disabled={loading}
        >
          {loading ? "Actualizando..." : "Actualizar"}
        </button>
      </section>

      {canManageInventory ? (
        <section className="inventory-count-card">
          <div className="inventory-count-card-heading">
            <div>
              <h2>Abrir conteo físico</h2>
              <p>
                Puedes contar todos los productos o una sola
                categoría de una bodega.
              </p>
            </div>
          </div>

          <form onSubmit={createCount}>
            <div className="inventory-count-form-grid">
              <label>
                Bodega
                <select
                  name="warehouse_id"
                  value={countForm.warehouse_id}
                  onChange={handleFormChange}
                  required
                >
                  <option value="">
                    Selecciona una bodega
                  </option>

                  {warehouses.map((warehouse) => (
                    <option
                      key={warehouse.id}
                      value={warehouse.id}
                    >
                      {warehouse.name}
                      {warehouse.is_default
                        ? " · Principal"
                        : ""}
                    </option>
                  ))}
                </select>
              </label>

              <label>
                Fecha de conteo
                <input
                  type="date"
                  name="count_date"
                  value={countForm.count_date}
                  onChange={handleFormChange}
                  required
                />
              </label>
            </div>

            <div className="inventory-count-options">
              <label>
                Categoría a contar
                <input
                  type="text"
                  name="category"
                  value={countForm.category}
                  onChange={handleFormChange}
                  maxLength="120"
                  placeholder="Opcional: deja vacío para contar todo"
                />
              </label>

              <label className="inventory-count-blind-option">
                <input
                  type="checkbox"
                  name="is_blind"
                  checked={countForm.is_blind}
                  onChange={handleFormChange}
                />

                <span>
                  <strong>Conteo ciego</strong>
                  <small>
                    Oculta el inventario teórico y las
                    diferencias hasta que un supervisor apruebe.
                  </small>
                </span>
              </label>
            </div>

            <label className="inventory-count-notes">
              Observaciones
              <textarea
                name="notes"
                rows="2"
                value={countForm.notes}
                onChange={handleFormChange}
                placeholder="Responsable, turno o información adicional"
              />
            </label>

            <button
              type="submit"
              className="btn-count-create"
              disabled={savingCount}
            >
              {savingCount
                ? "Abriendo conteo..."
                : "Abrir conteo físico"}
            </button>
          </form>
        </section>
      ) : (
        <section className="inventory-count-readonly">
          No tienes permiso para abrir o gestionar conteos
          físicos.
        </section>
      )}

      {canManageInventory && (
        <section className="inventory-count-card">
          <div className="inventory-count-card-heading">
            <div>
              <h2>Conteos programados</h2>
              <p>
                Configura conteos semanales o mensuales por bodega
                y categoría.
              </p>
            </div>
          </div>

          <form onSubmit={createSchedule}>
            <div className="inventory-count-form-grid">
              <label>
                Bodega
                <select
                  name="warehouse_id"
                  value={scheduleForm.warehouse_id}
                  onChange={handleScheduleFormChange}
                  required
                >
                  <option value="">
                    Selecciona una bodega
                  </option>

                  {warehouses.map((warehouse) => (
                    <option
                      key={warehouse.id}
                      value={warehouse.id}
                    >
                      {warehouse.name}
                      {warehouse.is_default
                        ? " · Principal"
                        : ""}
                    </option>
                  ))}
                </select>
              </label>

              <label>
                Frecuencia
                <select
                  name="frequency"
                  value={scheduleForm.frequency}
                  onChange={handleScheduleFormChange}
                >
                  <option value="weekly">Semanal</option>
                  <option value="monthly">Mensual</option>
                </select>
              </label>

              {scheduleForm.frequency === "weekly" ? (
                <label>
                  Día de ejecución
                  <select
                    name="weekday"
                    value={scheduleForm.weekday}
                    onChange={handleScheduleFormChange}
                  >
                    {weekDays.map((day) => (
                      <option
                        key={day.value}
                        value={day.value}
                      >
                        {day.label}
                      </option>
                    ))}
                  </select>
                </label>
              ) : (
                <label>
                  Día del mes
                  <input
                    type="number"
                    name="month_day"
                    min="1"
                    max="31"
                    value={scheduleForm.month_day}
                    onChange={handleScheduleFormChange}
                    required
                  />
                </label>
              )}
            </div>

            <div className="inventory-count-options">
              <label>
                Categoría a contar
                <input
                  type="text"
                  name="category"
                  value={scheduleForm.category}
                  onChange={handleScheduleFormChange}
                  maxLength="120"
                  placeholder="Opcional: todos los productos"
                />
              </label>

              <label className="inventory-count-blind-option">
                <input
                  type="checkbox"
                  name="is_blind"
                  checked={scheduleForm.is_blind}
                  onChange={handleScheduleFormChange}
                />

                <span>
                  <strong>Conteo ciego</strong>
                  <small>
                    El inventario teórico se ocultará a quien
                    realice el conteo.
                  </small>
                </span>
              </label>
            </div>

            <label className="inventory-count-notes">
              Observaciones para cada conteo generado
              <textarea
                name="notes"
                rows="2"
                value={scheduleForm.notes}
                onChange={handleScheduleFormChange}
                placeholder="Ejemplo: Conteo mensual del turno de mañana"
              />
            </label>

            <button
              type="submit"
              className="btn-count-create"
              disabled={savingSchedule}
            >
              {savingSchedule
                ? "Guardando programación..."
                : "Crear programación"}
            </button>
          </form>

          <div className="inventory-count-table-wrapper">
            <table className="inventory-count-table">
              <thead>
                <tr>
                  <th>Bodega</th>
                  <th>Frecuencia</th>
                  <th>Categoría</th>
                  <th>Próxima ejecución</th>
                  <th>Última generación</th>
                  <th>Estado</th>
                  <th />
                </tr>
              </thead>

              <tbody>
                {schedules.length === 0 ? (
                  <tr>
                    <td
                      colSpan="7"
                      className="inventory-count-empty"
                    >
                      No hay conteos programados.
                    </td>
                  </tr>
                ) : (
                  schedules.map((schedule) => {
                    const weekDayLabel = weekDays.find(
                      (day) =>
                        Number(day.value) ===
                        Number(schedule.weekday)
                    )?.label;

                    const frequencyLabel =
                      schedule.frequency === "weekly"
                        ? `Semanal · ${weekDayLabel || ""}`
                        : `Mensual · Día ${schedule.month_day}`;

                    return (
                      <tr key={schedule.id}>
                        <td>{schedule.warehouse_name}</td>

                        <td>{frequencyLabel}</td>

                        <td>
                          {schedule.category || "Todos"}
                          {schedule.is_blind && (
                            <small>Conteo ciego</small>
                          )}
                        </td>

                        <td>
                          {formatDate(schedule.next_run_date)}
                        </td>

                        <td>
                          {schedule.last_generated_date
                            ? formatDate(
                              schedule.last_generated_date
                            )
                            : "Aún no generado"}
                        </td>

                        <td>
                          <span
                            className={`inventory-count-status ${schedule.is_active
                                ? "completed"
                                : "cancelled"
                              }`}
                          >
                            {schedule.is_active
                              ? "Activa"
                              : "Pausada"}
                          </span>
                        </td>

                        <td className="inventory-count-schedule-actions">
                          <button
                            type="button"
                            className="btn-count-view"
                            onClick={() =>
                              runScheduleNow(schedule)
                            }
                            disabled={
                              runningScheduleId === schedule.id ||
                              !schedule.is_active
                            }
                          >
                            {runningScheduleId === schedule.id
                              ? "Abriendo..."
                              : "Ejecutar ahora"}
                          </button>

                          <button
                            type="button"
                            className="btn-count-cancel"
                            onClick={() =>
                              toggleScheduleStatus(schedule)
                            }
                          >
                            {schedule.is_active
                              ? "Pausar"
                              : "Activar"}
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </section>
      )}

      <section className="inventory-count-card">
        <div className="inventory-count-card-heading">
          <div>
            <h2>Conteos registrados</h2>
            <p>
              Consulta avances, aprobaciones y ajustes
              aplicados.
            </p>
          </div>
        </div>

        <div className="inventory-count-table-wrapper">
          <table className="inventory-count-table">
            <thead>
              <tr>
                <th>Conteo</th>
                <th>Fecha</th>
                <th>Bodega</th>
                <th>Progreso</th>
                <th>Diferencias</th>
                <th>Estado</th>
                <th />
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td
                    colSpan="7"
                    className="inventory-count-empty"
                  >
                    Cargando conteos...
                  </td>
                </tr>
              ) : counts.length === 0 ? (
                <tr>
                  <td
                    colSpan="7"
                    className="inventory-count-empty"
                  >
                    No hay conteos físicos registrados.
                  </td>
                </tr>
              ) : (
                counts.map((count) => (
                  <tr key={count.id}>
                    <td>
                      <strong>{count.count_number}</strong>
                      {count.is_blind && (
                        <small>Conteo ciego</small>
                      )}
                    </td>

                    <td>{formatDate(count.count_date)}</td>

                    <td>
                      {count.warehouse_name}
                      {count.category && (
                        <small>
                          Categoría: {count.category}
                        </small>
                      )}
                    </td>

                    <td>
                      {count.counted_items} /{" "}
                      {count.total_items}
                    </td>

                    <td>
                      {count.items_with_difference === null
                        ? "Ocultas"
                        : count.items_with_difference}
                    </td>

                    <td>
                      <span
                        className={`inventory-count-status ${count.status}`}
                      >
                        {statusLabel(count.status)}
                      </span>
                    </td>

                    <td>
                      <button
                        type="button"
                        className="btn-count-view"
                        onClick={() =>
                          loadCountDetail(count.id)
                        }
                      >
                        Ver conteo
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>

      {(loadingDetail || selectedCount) && (
        <div
          className="inventory-count-modal-backdrop"
          onClick={closeCountDetail}
        >
          <section
            className="inventory-count-modal"
            onClick={(event) => event.stopPropagation()}
          >
            {loadingDetail ? (
              <p className="inventory-count-empty">
                Cargando conteo físico...
              </p>
            ) : (
              <>
                <div className="inventory-count-modal-header">
                  <div>
                    <p className="inventory-counts-kicker">
                      Conteo físico
                    </p>

                    <h2>{selectedCount.count_number}</h2>

                    <span>
                      {selectedCount.warehouse_name} ·{" "}
                      {formatDate(selectedCount.count_date)}
                    </span>

                    {selectedCount.is_blind && (
                      <small className="inventory-count-blind-label">
                        Conteo ciego
                      </small>
                    )}
                  </div>

                  <button
                    type="button"
                    className="btn-count-close"
                    onClick={closeCountDetail}
                    aria-label="Cerrar conteo"
                  >
                    ×
                  </button>
                </div>

                {selectedCount.status ===
                  "pending_approval" && (
                    <div className="inventory-count-review-banner">
                      Este conteo está bloqueado y pendiente de
                      aprobación por un supervisor.
                    </div>
                  )}

                {selectedCount.rejection_reason && (
                  <div className="inventory-count-rejection-banner">
                    <strong>Devuelto para corrección:</strong>{" "}
                    {selectedCount.rejection_reason}
                  </div>
                )}

                <section className="inventory-count-progress">
                  <article>
                    <span>Productos</span>
                    <strong>
                      {selectedCount.total_items}
                    </strong>
                  </article>

                  <article>
                    <span>Contados</span>
                    <strong>
                      {selectedCount.counted_items}
                    </strong>
                  </article>

                  <article>
                    <span>Pendientes</span>
                    <strong>
                      {selectedCount.pending_items}
                    </strong>
                  </article>

                  <article>
                    <span>Diferencias</span>
                    <strong>
                      {selectedCount.items_with_difference ===
                        null
                        ? "Ocultas"
                        : selectedCount.items_with_difference}
                    </strong>
                  </article>
                </section>

                {selectedCount.notes && (
                  <p className="inventory-count-modal-notes">
                    {selectedCount.notes}
                  </p>
                )}

                <input
                  className="inventory-count-search"
                  type="search"
                  value={itemSearch}
                  onChange={(event) =>
                    setItemSearch(event.target.value)
                  }
                  placeholder="Buscar producto o código"
                />

                <div className="inventory-count-table-wrapper">
                  <table className="inventory-count-table">
                    <thead>
                      <tr>
                        <th>Producto</th>

                        {!selectedCount.is_blind ||
                          selectedCount.status === "completed" ||
                          canApproveInventory ? (
                          <>
                            <th>Teórico inicial</th>
                            <th>Cantidad física</th>
                            <th>Diferencia</th>
                          </>
                        ) : (
                          <th>Cantidad física</th>
                        )}

                        <th />
                      </tr>
                    </thead>

                    <tbody>
                      {filteredItems.map((item) => {
                        const canSeeTheoretical =
                          item.system_quantity_at_start !==
                          null &&
                          item.system_quantity_at_start !==
                          undefined;

                        const countedValue =
                          itemValues[item.id] ?? "";

                        const difference =
                          !canSeeTheoretical ||
                            countedValue === ""
                            ? null
                            : Number(countedValue) -
                            Number(
                              item.system_quantity_at_start
                            );

                        return (
                          <tr key={item.id}>
                            <td>
                              <strong>
                                {item.product_name}
                              </strong>
                              <small>{item.product_sku}</small>
                            </td>

                            {canSeeTheoretical && (
                              <td>
                                {formatQuantity(
                                  item.system_quantity_at_start
                                )}
                              </td>
                            )}

                            <td>
                              {selectedCount.status ===
                                "in_progress" ? (
                                <input
                                  type="number"
                                  min="0"
                                  step="0.001"
                                  value={countedValue}
                                  onChange={(event) =>
                                    setItemValues((current) => ({
                                      ...current,
                                      [item.id]:
                                        event.target.value,
                                    }))
                                  }
                                />
                              ) : (
                                formatQuantity(
                                  item.counted_quantity
                                )
                              )}
                            </td>

                            {canSeeTheoretical && (
                              <td
                                className={
                                  difference === null
                                    ? ""
                                    : difference > 0
                                      ? "count-positive"
                                      : difference < 0
                                        ? "count-negative"
                                        : ""
                                }
                              >
                                {difference === null
                                  ? "Pendiente"
                                  : `${difference > 0 ? "+" : ""
                                  }${formatQuantity(
                                    difference
                                  )}`}
                              </td>
                            )}

                            <td>
                              {selectedCount.status ===
                                "in_progress" &&
                                canManageInventory && (
                                  <button
                                    type="button"
                                    className="btn-count-save-item"
                                    disabled={
                                      savingItemId === item.id
                                    }
                                    onClick={() =>
                                      saveItemCount(item)
                                    }
                                  >
                                    {savingItemId === item.id
                                      ? "Guardando..."
                                      : "Guardar"}
                                  </button>
                                )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {selectedCount.status === "in_progress" &&
                  canManageInventory && (
                    <div className="inventory-count-modal-actions">
                      <button
                        type="button"
                        className="btn-count-cancel"
                        onClick={requestCancelCount}
                      >
                        Cancelar conteo
                      </button>

                      <button
                        type="button"
                        className="btn-count-complete"
                        onClick={requestSubmitCount}
                        disabled={
                          selectedCount.pending_items > 0
                        }
                      >
                        Enviar a aprobación
                      </button>
                    </div>
                  )}

                {selectedCount.status ===
                  "pending_approval" &&
                  canApproveInventory && (
                    <div className="inventory-count-modal-actions">
                      <button
                        type="button"
                        className="btn-count-cancel"
                        onClick={requestRejectCount}
                      >
                        Devolver para corrección
                      </button>

                      <button
                        type="button"
                        className="btn-count-complete"
                        onClick={requestApproveCount}
                      >
                        Aprobar y ajustar inventario
                      </button>
                    </div>
                  )}

                {selectedCount.status === "completed" && (
                  <div className="inventory-count-modal-actions">
                    <button
                      type="button"
                      className="btn-count-export"
                      onClick={exportCountToExcel}
                    >
                      Exportar Excel
                    </button>

                    <button
                      type="button"
                      className="btn-count-print"
                      onClick={printCountReceipt}
                    >
                      Imprimir / Guardar PDF
                    </button>

                    <button
                      type="button"
                      className="btn-count-close-secondary"
                      onClick={closeCountDetail}
                    >
                      Cerrar
                    </button>
                  </div>
                )}
              </>
            )}
          </section>
        </div>
      )}

      {confirmation && (
        <div
          className="count-confirmation-backdrop"
          onClick={() => {
            if (!processingConfirmation) {
              setConfirmation(null);
            }
          }}
        >
          <section
            className="count-confirmation-modal"
            onClick={(event) => event.stopPropagation()}
          >
            <div
              className={`count-confirmation-icon ${confirmation.type}`}
            >
              {confirmation.type === "cancel" ||
                confirmation.type === "reject"
                ? "!"
                : "✓"}
            </div>

            <p className="inventory-counts-kicker">
              Confirmación requerida
            </p>

            <h3>{confirmation.title}</h3>

            <p className="count-confirmation-message">
              {confirmation.message}
            </p>

            {confirmation.type === "approve" && (
              <div className="count-confirmation-warning">
                Esta acción aplicará los ajustes de inventario
                y no se puede deshacer.
              </div>
            )}

            {confirmation.type === "reject" && (
              <label className="count-rejection-reason">
                Motivo de devolución
                <textarea
                  rows="3"
                  value={confirmation.reason}
                  onChange={(event) =>
                    setConfirmation((current) => ({
                      ...current,
                      reason: event.target.value,
                    }))
                  }
                  placeholder="Ejemplo: revisa la cantidad física del producto..."
                  disabled={processingConfirmation}
                />
              </label>
            )}

            <div className="count-confirmation-actions">
              <button
                type="button"
                className="btn-count-confirm-cancel"
                onClick={() => setConfirmation(null)}
                disabled={processingConfirmation}
              >
                Volver
              </button>

              <button
                type="button"
                className={
                  confirmation.type === "cancel" ||
                    confirmation.type === "reject"
                    ? "btn-count-confirm-danger"
                    : "btn-count-confirm-complete"
                }
                onClick={confirmCountAction}
                disabled={processingConfirmation}
              >
                {processingConfirmation
                  ? "Procesando..."
                  : confirmation.confirmLabel}
              </button>
            </div>
          </section>
        </div>
      )}
    </div>
  );
}

export default CompanyInventoryCounts;